import React, { useMemo } from "react";
import CommonModal from "../CommonModal";
import Button from "../../Elements/Button";
import { utilsUpdateLocalStorage } from "../../../utis/utilsUpdateLocalStorage";
import { csrfToken } from "../../../utis/csrfTokenUtils";
import { networkService } from "../../config/axios";
import {
  COOKIE_CONSENT_DEFAULT_CONFIG,
} from "../../../constants/cookieConsentDefaults";

const CookieConsentModal = ({
  customizationText,
  concentModalStatus,
  setConcentModalStatus,
  customizationLink,
  companyPrivacyUrl,
  sessId,
  modalDetails,
  buttonStyles,
}) => {

  const config = useMemo(() => {
    const trimOrFallback = (value, fallback) => {
      if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed.length ? trimmed : fallback;
      }
      return fallback;
    };

    const mergedModal = {
      ...COOKIE_CONSENT_DEFAULT_CONFIG.modal_details,
      ...(modalDetails || {}),
    };

    mergedModal.border_weight =
      Number(mergedModal.border_weight) ||
      COOKIE_CONSENT_DEFAULT_CONFIG.modal_details.border_weight;
    mergedModal.bg_color =
      trimOrFallback(
        mergedModal.bg_color,
        COOKIE_CONSENT_DEFAULT_CONFIG.modal_details.bg_color
      );
    mergedModal.border_color =
      trimOrFallback(
        mergedModal.border_color,
        COOKIE_CONSENT_DEFAULT_CONFIG.modal_details.border_color
      );

    const mergedValue = {
      ...COOKIE_CONSENT_DEFAULT_CONFIG.value,
      ...(buttonStyles || {}),
    };

    Object.keys(mergedValue).forEach((key) => {
      mergedValue[key] = trimOrFallback(mergedValue[key], COOKIE_CONSENT_DEFAULT_CONFIG.value[key]);
    });

    const mergedLink = {
      ...COOKIE_CONSENT_DEFAULT_CONFIG.link_details,
      ...(customizationLink || {}),
    };
    mergedLink.link_text = trimOrFallback(
      mergedLink.link_text,
      COOKIE_CONSENT_DEFAULT_CONFIG.link_details.link_text
    );
    mergedLink.link_url = trimOrFallback(
      mergedLink.link_url,
      companyPrivacyUrl || COOKIE_CONSENT_DEFAULT_CONFIG.link_details.link_url
    );

    const text = trimOrFallback(
      customizationText,
      COOKIE_CONSENT_DEFAULT_CONFIG.pp_text
    );

    return {
      pp_text: text,
      link_details: mergedLink,
      modal_details: mergedModal,
      value: mergedValue,
    };
  }, [customizationText, customizationLink, modalDetails, buttonStyles]);

  const handleCookieConsent = async (statusKey) => {
    utilsUpdateLocalStorage("cookiesPrivacy", statusKey);

    const payload = {
      authenticity_token: csrfToken(),
      cookie_status: statusKey,   // Must match backend
      session_id: sessId,
    };

    try {
      await networkService(`viewer/cookie_consent`, "POST", payload);
      // Update the session object in window.objInit so it's available on refresh
      if (window?.objInit?.session) {
        window.objInit.session.cookie_status = statusKey;
      }
      
      // Dispatch custom event to notify components that cookie status has changed
      // This allows components to re-render when cookie consent changes
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
          detail: { cookieStatus: statusKey } 
        }));
      }
    } catch (error) {
      console.error("Failed to save cookie consent:", error);
    }
  };

  const handleClick = (statusKey) => {
    setConcentModalStatus(false);
    handleCookieConsent(statusKey);
  };

  const handleRejectViaClose = () => {
    handleClick("rejected");
  };

  const borderWeight = config.modal_details.border_weight || 0;

  return (
    <CommonModal
      className="req-concent"
      modalStatus={concentModalStatus}
      title={""}
      classList={"concent-modal"}
      changeActiveStatus={(status) => {
        if (status === false) {
          handleRejectViaClose();
        }
      }}
    >
      <div
        style={{
          border: `${borderWeight}px solid ${config.modal_details.border_color}`,
          background: config.modal_details.bg_color,
          padding: "45px 28px 28px",
          borderRadius: "12px",
          boxShadow: "0px 18px 24px rgba(0,0,0,0.08)",
          // minWidth: "320px",
        }}
      >
        <p className="mt-2 mb-3" style={{ lineHeight: 1.5, color: "#333" }}>
          {config.pp_text}{" "}
          <a
            className="privacy"
            target="_blank"
            rel="noreferrer"
            href={companyPrivacyUrl || config.link_details.link_url || "https://kinetizine.com/privacy"}
          >
            {config.link_details.link_text}
          </a>
        </p>

        <div className="d-flex align-items-center justify-content-center gap-3 Act-btn flex-wrap text-center">
          <Button
            classList={"outline"}
            text={"Accept only essential cookies"}
            addonClass={"my-2"}
            style={{
              background: config.value.btn1_bg_color,
              color: config.value.btn1_text_color,
              borderColor: config.value.btn1_border_color,
              border: `1px solid ${config.value.btn1_border_color}`,
              minWidth: "180px",
              width: "100%",
            }}
            onClick={() => handleClick("essential_only")}
          />

          <Button
            classList={"success"}
            text={"Accept all cookies"}
            addonClass={"my-2"}
            style={{
              background: config.value.btn2_bg_color,
              color: config.value.btn2_text_color,
              borderColor: config.value.btn2_border_color,
              border: `1px solid ${config.value.btn2_border_color}`,
              boxShadow: "none",
              minWidth: "180px",
            }}
            onClick={() => handleClick("accepted_all")}
          />
        </div>
      </div>
    </CommonModal>
  );
};

export default CookieConsentModal;
